export interface FactoryLocation {
  lat: number;
  lng: number;
  postal: string;
  label: string;
}

export interface GlobalSettings {
  gstRate: number;
  deliveryFee: number;
  minOrderCharge: number;
  cbdSurcharge: number;
  mmToInchDivisor: number; // strictly 25.4
  sqFtDivisor: number; // strictly 144
  quoteValidityDays: number;
  /** Email of the measurer auto-assigned to every new Measurement
   *  Confirmation, stamped onto `assignedMeasurer` at MC creation. Lets a
   *  customer-self-booked measurement be worked without a manual dispatch
   *  step. Empty = assign manually. Stored lowercased to match the
   *  firestore.rules measurer gate (`assignedMeasurer == emailLower()`). */
  defaultMeasurer: string;
  /** Origin/return point for the delivery route planner. Editable from the
   *  System Settings tab so the team can move warehouse without a deploy.
   *  The seed below is the real Senoko factory (11 Senoko Drive, S758199,
   *  OneMap-verified 2026-06-11); the admin UI still overrides at runtime. */
  factoryLocation: FactoryLocation;
  /** Admin-maintained monthly MAS average FX rates, SGD per 1 unit foreign,
   *  keyed YYYY-MM. Consumed by receipt currency conversion (Phase B). */
  fxRates?: Record<string, { MYR?: number; CNY?: number }>;
}

export const GLOBAL_SETTINGS: GlobalSettings = {
  gstRate: 0.09,
  deliveryFee: 150,
  minOrderCharge: 300,
  cbdSurcharge: 50,
  mmToInchDivisor: 25.4,
  sqFtDivisor: 144,
  quoteValidityDays: 30,
  defaultMeasurer: '',
  factoryLocation: {
    lat: 1.459267,
    lng: 103.797199,
    postal: '758199',
    label: 'WSS factory — 11 Senoko Drive',
  },
  fxRates: {
    '2026-05': { MYR: 0.31, CNY: 0.2 },
  },
};

// Reactive version counter for the mutable GLOBAL_SETTINGS object. Mirrors
// the catalog pub/sub in products.ts. GLOBAL_SETTINGS is Object.assigned in
// place by settingsService — without a separate signal, mounted components
// that snapshot it once would never re-evaluate after Firestore hydrates.
let settingsVersion = 0;
const settingsListeners = new Set<() => void>();

export const bumpSettingsVersion = () => {
  settingsVersion++;
  settingsListeners.forEach((cb) => cb());
};
export const subscribeSettings = (cb: () => void) => {
  settingsListeners.add(cb);
  return () => {
    settingsListeners.delete(cb);
  };
};
export const getSettingsVersion = () => settingsVersion;
